/**
 * Sidebar navigation rail.
 *
 * Refinements over the original:
 *   - Glass surface (tier2) for the rail background
 *   - Active item tinted by pillarAccent[getPillarForRoute(pathname)]
 *   - Hover affordance: framer-motion spring scale + slight x-shift
 *   - Keyboard nav: arrow keys cycle through visible items; '/' focuses
 *     the command palette input via setCommandPaletteOpen
 *   - Collapsed state: icon-only rail with Tooltip on each item
 *   - Brand mark: hover spring scale
 *   - prefetchRouteData called on onMouseEnter (80ms debounce from WS-1)
 *
 * Do not add em-dashes anywhere.
 */

import type { CSSProperties, KeyboardEvent } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useRef, useCallback, useEffect, useMemo } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import {
  LayoutDashboard,
  Activity,
  BarChart3,
  GitCompare,
  FileInput,
  Scale,
  Bell,
  PanelLeftClose,
  PanelLeftOpen,
  Home,
  Zap,
  PieChart,
  TrendingUp,
  ShieldCheck,
  Wrench,
  BarChart2,
  Layers,
  Sigma,
  BrainCircuit,
  FlaskConical,
  Send,
  User,
  LogOut,
  Sparkles,
  Sliders,
  Users,
  Banknote,
  Terminal as TerminalIcon,
  TrendingDown,
  Compass,
  Podcast,
  Gauge,
  Inbox,
  Database,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { elevation, pillarAccent, z, type as typeTokens, color } from '@/design-system/tokens'
import { useAppStore } from '@/stores/app'
import { useAuth } from '@/contexts/AuthContext'
import { isOwner } from '@/components/auth/OwnerRoute'
import { prefetchRouteData } from '@/app/queryClient'
import { prefetchRouteChunk } from '@/app/routeLoaders'
import { getPillarForRoute } from './pillar'
import { cn } from '@/lib/utils'

// ---------------------------------------------------------------------------
// Nav data (same shape as original)
// ---------------------------------------------------------------------------
const NAV_SECTIONS = [
  {
    label: 'Research',
    items: [
      { to: '/dashboard',    label: 'Dashboard',    icon: LayoutDashboard, ownerOnly: false },
      { to: '/terminal',     label: 'Terminal',     icon: TerminalIcon,    ownerOnly: false },
      { to: '/screen',       label: 'Screen',       icon: BarChart3,       ownerOnly: false },
      { to: '/compare',      label: 'Compare',      icon: GitCompare,      ownerOnly: false },
      { to: '/manual-input', label: 'Manual Input', icon: FileInput,       ownerOnly: false },
      { to: '/mna',          label: 'M&A Analyzer', icon: Scale,           ownerOnly: false },
      { to: '/apophis',      label: 'Apophis',      icon: Send,            ownerOnly: false },
      { to: '/apophis/documents', label: 'Documents', icon: Layers,        ownerOnly: false },
      { to: '/peer-groups',  label: 'Peer Groups',  icon: Users,           ownerOnly: false },
      { to: '/research-inbox', label: 'Research Inbox', icon: Inbox,       ownerOnly: false },
    ],
  },
  {
    label: 'Portfolio',
    items: [
      { to: '/watchlist',      label: 'Watchlist',      icon: Bell,        ownerOnly: false },
      { to: '/events',         label: 'Events',          icon: Zap,         ownerOnly: false },
      { to: '/portfolio',      label: 'Portfolio',       icon: PieChart,    ownerOnly: false },
      { to: '/estimates',      label: 'Estimates',       icon: TrendingUp,  ownerOnly: false },
    ],
  },
  {
    label: 'Intelligence',
    items: [
      { to: '/backtesting',     label: 'Track Record',     icon: BarChart2,    ownerOnly: false },
      { to: '/factors',         label: 'Factor Analytics', icon: Sigma,        ownerOnly: false },
      { to: '/ml-insights',     label: 'ML Insights',      icon: BrainCircuit, ownerOnly: false },
      { to: '/strategies',      label: 'Strategies',       icon: FlaskConical, ownerOnly: false },
      { to: '/moat',            label: 'Moat Analysis',    icon: Sparkles,     ownerOnly: false },
      { to: '/credit-universe', label: 'Credit Universe',  icon: Banknote,     ownerOnly: false },
      { to: '/crowded-trades',  label: 'Crowded Trades',   icon: Users,        ownerOnly: false },
      { to: '/contrarian',     label: 'Contrarian',       icon: TrendingDown, ownerOnly: false },
      { to: '/thematic',       label: 'Thematic',         icon: Compass,      ownerOnly: false },
      { to: '/podcasts',       label: 'Podcasts',         icon: Podcast,      ownerOnly: false },
      { to: '/sector-skills',  label: 'Sector Skills',    icon: Gauge,        ownerOnly: false },
      { to: '/batch',           label: 'Batch Analysis',   icon: Layers,       ownerOnly: false },
      { to: '/calibration',     label: 'Calibration',      icon: Sliders,      ownerOnly: true },
    ],
  },
  {
    label: 'Compliance',
    items: [
      { to: '/compliance', label: 'Compliance', icon: ShieldCheck, ownerOnly: false },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/analytics',       label: 'Analytics',       icon: Activity, ownerOnly: true },
      { to: '/data-pipelines', label: 'Data Pipelines', icon: Database, ownerOnly: true },
      { to: '/devtools',       label: 'Dev Tools',       icon: Wrench,   ownerOnly: true },
      { to: '/settings',       label: 'Settings',        icon: User,     ownerOnly: true },
    ],
  },
]

// ---------------------------------------------------------------------------
// NavItem component with spring hover
// ---------------------------------------------------------------------------
interface NavItemProps {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string; style?: CSSProperties }>
  isActive: boolean
  accent: string
  collapsed: boolean
  tabIndex?: number
  onKeyDown?: (e: KeyboardEvent<HTMLAnchorElement>) => void
  onHoverEnter: () => void
  onHoverLeave: () => void
}

function NavItem({
  to,
  label,
  icon: Icon,
  isActive,
  accent,
  collapsed,
  tabIndex,
  onKeyDown,
  onHoverEnter,
  onHoverLeave,
}: NavItemProps) {
  const shouldReduceMotion = useReducedMotion()

  const baseStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: collapsed ? 0 : '10px',
    justifyContent: collapsed ? 'center' : 'flex-start',
    borderRadius: '6px',
    padding: collapsed ? '8px' : '8px 12px',
    fontSize: `${typeTokens.bodySmall.size}px`,
    fontWeight: 500,
    textDecoration: 'none',
    transition: 'color 120ms ease, background-color 120ms ease',
    borderLeft: isActive ? `2px solid ${accent}` : '2px solid transparent',
    paddingLeft: isActive ? (collapsed ? '7px' : '10px') : (collapsed ? '8px' : '12px'),
    color: isActive ? accent : color.graySecondary,
    background: isActive ? hexToRgba(accent, 0.07) : 'transparent',
    cursor: 'pointer',
  }

  return (
    <motion.div
      whileHover={
        shouldReduceMotion
          ? {}
          : {
              scale: 1.02,
              x: 1,
              transition: { type: 'spring', stiffness: 400, damping: 25 },
            }
      }
      style={{ width: '100%' }}
    >
      <NavLink
        to={to}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
        onMouseEnter={onHoverEnter}
        onMouseLeave={onHoverLeave}
        style={baseStyle}
        data-nav-item="true"
      >
        <Icon
          className="shrink-0"
          style={{ width: '16px', height: '16px', color: isActive ? accent : undefined }}
        />
        {!collapsed && <span>{label}</span>}
      </NavLink>
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

export default function Sidebar() {
  const sidebarOpen = useAppStore((s) => s.sidebarOpen)
  const toggleSidebar = useAppStore((s) => s.toggleSidebar)
  const setCommandPaletteOpen = useAppStore((s) => s.setCommandPaletteOpen)
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const shouldReduceMotion = useReducedMotion()

  // Pillar accent for the current route
  const pillar = getPillarForRoute(pathname)
  const accent = pillarAccent[pillar]

  // Flatten nav items for keyboard navigation
  const visibleItems = useMemo(() => {
    return NAV_SECTIONS.flatMap((section) =>
      section.items.filter(({ ownerOnly }) => !ownerOnly || isOwner(user?.email)),
    )
  }, [user?.email])

  // The active nav item is the *longest* prefix match of the current path.
  // Without this, e.g. `/apophis/documents` lights up both `/apophis` and
  // `/apophis/documents`; picking only the most specific match keeps each
  // item standalone.
  const activeRoute = useMemo(() => {
    const matches = NAV_SECTIONS.flatMap((section) => section.items)
      .map((item) => item.to)
      .filter((to) => pathname === to || pathname.startsWith(to + '/'))
      .sort((a, b) => b.length - a.length)
    return matches[0] ?? null
  }, [pathname])

  // Track focused item index for arrow-key navigation
  const focusedIndexRef = useRef<number>(-1)
  const itemRefs = useRef<HTMLAnchorElement[]>([])

  const registerRef = useCallback((el: HTMLAnchorElement | null, idx: number) => {
    if (el) itemRefs.current[idx] = el
  }, [])

  const handleSidebarKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === '/') {
        e.preventDefault()
        setCommandPaletteOpen(true)
        return
      }

      const items = itemRefs.current.filter(Boolean)
      if (items.length === 0) return

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        focusedIndexRef.current = (focusedIndexRef.current + 1) % items.length
        items[focusedIndexRef.current]?.focus()
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        focusedIndexRef.current =
          (focusedIndexRef.current - 1 + items.length) % items.length
        items[focusedIndexRef.current]?.focus()
      }
    },
    [setCommandPaletteOpen],
  )

  // Debounced prefetch on hover
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleNavHover = useCallback((to: string) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    // Fire chunk prefetch immediately (no debounce): the network fetch is
    // idempotent and dedup'd in routeLoaders, and starting it 80ms earlier
    // is the whole point.
    prefetchRouteChunk(to)
    hoverTimer.current = setTimeout(() => {
      prefetchRouteData(to)
    }, 80)
  }, [])
  const handleNavHoverEnd = useCallback(() => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current)
      hoverTimer.current = null
    }
  }, [])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current)
    }
  }, [])

  const sidebarStyle: CSSProperties = {
    position: 'sticky',
    top: 0,
    left: 0,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    zIndex: z.sidebar,
    width: sidebarOpen ? '256px' : '64px',
    transition: shouldReduceMotion ? 'none' : 'width 200ms ease-in-out',
    ...elevation.tier2,
    borderRight: elevation.tier2.border,
    borderTop: 'none',
    borderBottom: 'none',
    borderLeft: 'none',
    overflowY: 'auto',
    overflowX: 'hidden',
  }

  let itemIdx = 0

  return (
    <TooltipProvider>
      <aside
        style={sidebarStyle}
        aria-label="Sidebar"
        onKeyDown={handleSidebarKeyDown}
      >
        {/* Brand mark */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: '56px',
            padding: '0 16px',
            flexShrink: 0,
          }}
        >
          <motion.span
            style={{
              color: accent,
              fontWeight: 600,
              fontSize: '13px',
              letterSpacing: '0.2em',
              userSelect: 'none',
              cursor: 'default',
              display: 'block',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              ...(sidebarOpen ? {} : { margin: '0 auto' }),
            }}
            whileHover={
              shouldReduceMotion
                ? {}
                : { scale: 1.06, transition: { type: 'spring', stiffness: 400, damping: 20 } }
            }
          >
            {sidebarOpen ? 'LEVIATHAN' : 'L'}
          </motion.span>
        </div>

        {/* Accent divider */}
        <div
          style={{
            margin: '0 12px',
            height: '1px',
            background: hexToRgba(accent, 0.3),
            flexShrink: 0,
          }}
        />

        {/* Navigation */}
        <nav
          aria-label="Main navigation"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            padding: '8px',
            gap: '2px',
          }}
        >
          {NAV_SECTIONS.map((section) => {
            const sectionItems = section.items.filter(
              ({ ownerOnly }) => !ownerOnly || isOwner(user?.email),
            )
            if (sectionItems.length === 0) return null

            return (
              <div key={section.label} style={{ marginBottom: '4px' }}>
                {sidebarOpen ? (
                  <p
                    style={{
                      padding: '8px 12px 4px',
                      fontSize: `${typeTokens.eyebrow.size}px`,
                      fontWeight: typeTokens.eyebrow.weight,
                      letterSpacing: typeTokens.eyebrow.tracking,
                      textTransform: 'uppercase',
                      // WCAG AA: use grayPrimary (#A0A0B4) at full opacity
                      // instead of a translucent graySecondary, which drops to
                      // ~1.65:1 over tier2 glass.
                      color: color.grayPrimary,
                      userSelect: 'none',
                      margin: 0,
                    }}
                  >
                    {section.label}
                  </p>
                ) : (
                  <div
                    style={{
                      margin: '6px 12px',
                      height: '1px',
                      background: hexToRgba(color.grayBorder, 0.4),
                    }}
                  />
                )}
                {sectionItems.map(({ to, label, icon: Icon }) => {
                  const isActive = to === activeRoute
                  const currentIdx = itemIdx++
                  const navItem = (
                    <NavItem
                      key={to}
                      to={to}
                      label={label}
                      icon={Icon}
                      isActive={isActive}
                      accent={accent}
                      collapsed={!sidebarOpen}
                      tabIndex={0}
                      onHoverEnter={() => handleNavHover(to)}
                      onHoverLeave={handleNavHoverEnd}
                      onKeyDown={(e) => handleSidebarKeyDown(e as unknown as KeyboardEvent<HTMLElement>)}
                    />
                  )

                  if (!sidebarOpen) {
                    return (
                      <Tooltip key={to}>
                        <TooltipTrigger render={
                          <div ref={(el) => registerRef(el?.querySelector('a') ?? null, currentIdx)}>
                            {navItem}
                          </div>
                        } />
                        <TooltipContent side="right">
                          <span>{label}</span>
                        </TooltipContent>
                      </Tooltip>
                    )
                  }

                  return (
                    <div
                      key={to}
                      ref={(el) => registerRef(el?.querySelector('a') ?? null, currentIdx)}
                    >
                      {navItem}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </nav>

        {/* Bottom section */}
        <div
          style={{
            padding: '0 8px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              margin: '4px 4px',
              height: '1px',
              background: hexToRgba(color.grayBorder, 0.6),
            }}
          />

          {/* User info + profile link */}
          {user && (
            <button
              onClick={() => navigate('/profile')}
              className={cn(
                'flex items-center rounded-md px-3 py-2 text-sm font-medium text-gray-secondary hover:text-white-soft transition-colors border-l-2 border-transparent',
                !sidebarOpen && 'justify-center px-0',
              )}
              style={{ gap: sidebarOpen ? '10px' : 0, cursor: 'pointer' }}
            >
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: hexToRgba(color.gold, 0.15),
                  border: `1px solid ${hexToRgba(color.gold, 0.4)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <User style={{ width: '12px', height: '12px', color: color.gold }} />
              </div>
              {sidebarOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                  <span style={{ fontSize: '12px', color: color.whiteSoft, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
                    {user.name ?? 'User'}
                  </span>
                  <span style={{ fontSize: '10px', color: color.graySecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
                    {user.email}
                  </span>
                </div>
              )}
            </button>
          )}

          {/* Logout */}
          <button
            onClick={async () => { await logout(); navigate('/login') }}
            aria-label="Sign out"
            className={cn(
              'flex items-center rounded-md px-3 py-2 text-sm font-medium text-gray-secondary hover:text-red-400 transition-colors border-l-2 border-transparent',
              !sidebarOpen && 'justify-center px-0',
            )}
            style={{ gap: sidebarOpen ? '10px' : 0, cursor: 'pointer', background: 'transparent', border: 'none' }}
          >
            <LogOut style={{ width: '16px', height: '16px', flexShrink: 0 }} />
            {sidebarOpen && <span>Sign out</span>}
          </button>

          {/* Home link */}
          <button
            onClick={() => navigate('/')}
            aria-label="Home"
            className={cn(
              'flex items-center rounded-md px-3 py-2 text-sm font-medium text-gray-secondary hover:text-white-soft transition-colors border-l-2 border-transparent',
              !sidebarOpen && 'justify-center px-0',
            )}
            style={{ gap: sidebarOpen ? '10px' : 0, cursor: 'pointer', background: 'transparent', border: 'none' }}
          >
            <Home style={{ width: '16px', height: '16px', flexShrink: 0 }} />
            {sidebarOpen && <span>Home</span>}
          </button>

          {/* Collapse toggle */}
          <Button
            variant="ghost"
            size={sidebarOpen ? 'default' : 'icon'}
            onClick={toggleSidebar}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-expanded={sidebarOpen}
            className={cn(
              'text-gray-secondary hover:text-white-soft',
              sidebarOpen ? 'justify-start gap-3 px-3' : 'mx-auto',
            )}
          >
            {sidebarOpen ? (
              <>
                <PanelLeftClose className="size-4 shrink-0" />
                <span className="text-sm">Collapse</span>
              </>
            ) : (
              <PanelLeftOpen className="size-4 shrink-0" />
            )}
          </Button>
        </div>
      </aside>
    </TooltipProvider>
  )
}

/** Inline hexToRgba helper */
function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '')
  const r = parseInt(clean.slice(0, 2), 16)
  const g = parseInt(clean.slice(2, 4), 16)
  const b = parseInt(clean.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
